<template>
    <div class="w-100 align-self-start fill-height">
        <v-card flat color="transparent" class="mb-14">
            <v-card-title>
                <span class="secondary--text">New {{ selectedSpaceInfo.type }}</span>
            </v-card-title>
            <v-card-text>
                <v-form ref="form" v-model="valid">
                    <v-row>
                        <v-col cols="12">
                            <v-card>
                                <v-card-title>Space details</v-card-title>
                                <v-card-text>
                                    <div>
                                        <v-row align="start">
                                            <v-col :cols="sidToClone ? 12 : 8">
                                                <v-text-field
                                                    v-model="spaceName"
                                                    dense
                                                    :label="`${selectedSpaceInfo.type} name*`"
                                                    :counter="50"
                                                    :rules="[rules.nonEmpty]"
                                                    required
                                                    :disabled="loading || fetchingSpaces"
                                                    autofocus
                                                    hide-details
                                                    outlined></v-text-field>
                                            </v-col>
                                            <v-col cols="4" v-if="!sidToClone">
                                                <v-select
                                                    v-model="spaceVisibility"
                                                    dense
                                                    :items="spaceVisibilityTypes"
                                                    item-text="label"
                                                    item-value="label"
                                                    label="Visibility"
                                                    outlined
                                                    hide-details
                                                    :disabled="loading || fetchingSpaces">
                                                    <template v-slot:selection="{ item }">
                                                        <span class="font-weight-medium">{{ item.label }}</span>
                                                    </template>
                                                    <template v-slot:item="{ item }">
                                                        <div class="d-flex flex-column py-1">
                                                            <span class="body-2 font-weight-medium">{{ item.label }}</span>
                                                            <span class="caption secondary--text text-wrap">{{ item.description }}</span>
                                                        </div>
                                                    </template>
                                                </v-select>
                                            </v-col>
                                        </v-row>
                                        <ShortIdInput :longId="spaceName" @input="updateShortId($event.nextValue)"></ShortIdInput>
                                    </div>
                                    <v-row class="mt-2">
                                        <v-col :cols="newSpaceType === spaceTypes.EDUCATION_SPACE ? 8 : 12">
                                            <v-textarea
                                                v-model="spaceDescription"
                                                dense
                                                label="Short description*"
                                                rows="1"
                                                auto-grow
                                                hide-details
                                                :rules="[rules.nonEmpty]"
                                                required
                                                :disabled="loading || fetchingSpaces"
                                                outlined></v-textarea>
                                        </v-col>
                                        <v-col v-if="newSpaceType === spaceTypes.EDUCATION_SPACE" cols="4">
                                            <v-menu
                                                v-model="spaceArchiveDateMenu"
                                                :close-on-content-click="false"
                                                :nudge-right="40"
                                                :z-index="30"
                                                transition="scale-transition"
                                                offset-y
                                                min-width="auto">
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-text-field
                                                        v-model="spaceArchiveByDate"
                                                        dense
                                                        readonly
                                                        hide-details
                                                        label="Space archival date"
                                                        :rules="[rules.nonEmpty]"
                                                        required
                                                        v-bind="attrs"
                                                        v-on="on"
                                                        outlined></v-text-field>
                                                </template>
                                                <v-date-picker v-model="spaceArchiveByDate" @input="spaceArchiveDateMenu = false"></v-date-picker>
                                            </v-menu>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </v-col>
                        <v-col cols="12">
                            <v-card>
                                <v-card-title>Initial content</v-card-title>
                                <v-card-subtitle>Choose how the new space should be initialized.</v-card-subtitle>
                                <v-card-text>
                                    <v-radio-group
                                        row
                                        v-model="spaceSetupMode"
                                        id="spaceSetupMode"
                                        hide-details
                                        :disabled="loading || fetchingSpaces"
                                        class="mt-0">
                                        <v-row style="justify-content: space-around">
                                            <v-col>
                                                <v-card flat :color="spaceSetupMode === 'readme' ? 'grey lighten-3' : ''" outlined>
                                                    <v-card-text>
                                                        <v-radio value="readme" class="pl-4">
                                                            <template v-slot:label>
                                                                <div class="d-flex flex-column ml-2">
                                                                    <span class="font-weight-medium">Add README</span>
                                                                    <span class="secondary--text caption">
                                                                        Create an empty space and initialize it with a README file.
                                                                    </span>
                                                                </div>
                                                            </template>
                                                        </v-radio>
                                                    </v-card-text>
                                                </v-card>
                                            </v-col>
                                            <v-col>
                                                <v-card flat :color="spaceSetupMode === 'template' ? 'grey lighten-3' : ''" outlined>
                                                    <v-card-text>
                                                        <v-radio value="template" class="pl-4">
                                                            <template v-slot:label>
                                                                <div class="d-flex flex-column ml-2">
                                                                    <span class="font-weight-medium">Create space based on a template</span>
                                                                    <span class="secondary--text caption">
                                                                        Copy content from an existing space or clone a repository.
                                                                    </span>
                                                                </div>
                                                            </template>
                                                        </v-radio>
                                                    </v-card-text>
                                                </v-card>
                                            </v-col>
                                        </v-row>
                                    </v-radio-group>

                                    <template v-if="showCloneOptions">
                                        <div class="d-flex">
                                            <v-alert type="info" dense text class="mt-5 mb-0">
                                                Copying content to the new space will execute as a background task and
                                                <b>may take up to 10 minutes.</b>
                                            </v-alert>
                                        </div>
                                        <v-radio-group row v-model="cloneRadio" id="cloneRadio">
                                            <v-row style="justify-content: space-around">
                                                <v-col>
                                                    <v-card flat :color="cloneRadio === 'space' ? 'grey lighten-3' : ''" outlined>
                                                        <v-card-text>
                                                            <v-radio value="space" class="pl-4">
                                                                <template v-slot:label>
                                                                    <v-autocomplete
                                                                        style="z-index: 2"
                                                                        dense
                                                                        clearable
                                                                        :items="spacesWithCurrentType"
                                                                        :disabled="cloneRadio == 'repository' || fetchingSpaces || loading"
                                                                        :loading="fetchingSpaces"
                                                                        v-model="sidToClone"
                                                                        item-text="space_long_id"
                                                                        item-value="sid"
                                                                        placeholder="Clone an existing space"
                                                                        outlined
                                                                        class="ml-2"></v-autocomplete>
                                                                </template>
                                                            </v-radio>
                                                        </v-card-text>
                                                    </v-card>
                                                </v-col>
                                                <v-col>
                                                    <v-card flat :color="cloneRadio === 'repository' ? 'grey lighten-3' : ''" outlined>
                                                        <v-card-text>
                                                            <v-radio value="repository" class="pl-4">
                                                                <template v-slot:label>
                                                                    <v-text-field
                                                                        v-model="githubURL"
                                                                        dense
                                                                        :label="
                                                                            sidToClone
                                                                                ? 'Github repositories are only available in empty spaces'
                                                                                : 'Clone a Github repository'
                                                                        "
                                                                        :disabled="cloneRadio == 'space'"
                                                                        :rules="[rules.isGithubURL]"
                                                                        :hint="githubURLHint"
                                                                        persistent-hint
                                                                        outlined
                                                                        class="ml-2"></v-text-field>
                                                                </template>
                                                            </v-radio>
                                                        </v-card-text>
                                                    </v-card>
                                                </v-col>
                                            </v-row>
                                        </v-radio-group>
                                    </template>

                                    <div v-if="addReadme" class="mt-5">
                                        <MarkdownEditor
                                            :placeholderText="selectedSpaceInfo.readmePlaceholder"
                                            @newMarkdownContent="readmeFileContent = $event.value" />
                                    </div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-form>
                <v-alert :value="error" color="error" icon="warning" class="mt-4" text>
                    <v-row align="center">
                        <v-col class="grow">
                            <div class="font-weight-bold">{{ errorContent }}</div>
                            <span>For more information on space creation issues, check the troubleshooting documentation.</span>
                        </v-col>
                        <v-col class="shrink">
                            <v-btn
                                small
                                outlined
                                color="error"
                                href="https://docs.nuvolos.com/troubleshooting/troubleshooting/administration-troubleshooting/i-cant-create-a-space"
                                target="_blank">
                                Docs
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-alert>
            </v-card-text>
            <v-card-actions>
                <div>
                    <v-btn
                        color="white"
                        :to="{ name: 'home-dashboard', params: { oid: $route.params.oid } }"
                        class="mr-2"
                        :disabled="loading || fetchingSpaces">
                        cancel
                    </v-btn>
                </div>
                <v-spacer></v-spacer>
                <div>
                    <v-btn
                        :loading="loading || fetchingSpaces"
                        :disabled="!valid || loading || fetchingSpaces"
                        @click="sidToClone ? cloneSpace() : addSpace()"
                        color="primary">
                        {{ sidToClone ? 'Clone' : 'Add' }} Space
                    </v-btn>
                </div>
            </v-card-actions>
        </v-card>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import { enumsData } from '@/mixins/enums'
import { get } from 'lodash'
const proposedArchivalDate = new Date()
proposedArchivalDate.setMonth(proposedArchivalDate.getMonth() + 8)
const MarkdownEditor = () => import('@/components/MarkdownEditor')
const ShortIdInput = () => import('@/components/ShortIdInput')

export default {
    mixins: [enumsData],
    data() {
        return {
            addReadme: !this.$route.query.sid,
            spaceName: '',
            spaceShortName: '',
            spaceDescription: '',
            githubURL: '',
            spaceArchiveByDate: proposedArchivalDate.toISOString().split('T')[0],
            spaceArchiveDateMenu: false,
            showCloneOptions: !!this.$route.query.sid,
            spaceSetupMode: this.$route.query.sid ? 'template' : 'readme',
            cloneRadio: this.$route.query.sid ? 'space' : 'repository',
            sidToClone: parseInt(this.$route.query.sid) || null,
            spaceVisibility: 'Private',
            spaceVisibilityTypes: [
                {
                    label: 'Private',
                    code: 3,
                    description: 'Only you, the organization managers, and the people you explicitly invite can see the space.'
                },
                {
                    label: 'Faculty-only',
                    code: 2,
                    description: 'Only you, the organization managers, the people you explicitly invite, and faculty can see the space.'
                },
                {
                    label: 'Public',
                    code: 0,
                    description: 'Every user of this organization can see the space.'
                }
            ],
            error: false,
            errorContent: 'AN ERROR HAS OCCURRED',
            rules: {
                nonEmpty: p => p.length > 0 || 'Please provide a value.',
                isGithubURL(url) {
                    if (!url) return true
                    const githubEpxression = /((git|ssh|http(s)?)|(git@[\w.]+))(:(\/\/)?)([\w.@:/\-~]+)(\.git)(\/)?/
                    return url.match(githubEpxression) !== null
                }
            },
            loading: false,
            valid: false,
            readmeFileContent: ''
        }
    },
    components: {
        MarkdownEditor,
        ShortIdInput
    },
    props: {
        spaceTypeIcon: String,
        newSpaceType: Number
    },
    methods: {
        setSpaceCreationError(error) {
            const errorCode = get(error, 'response.data.code')
            const errorMsg = get(error, 'response.data.reason[0]')
            const spaceAlreadyExists =
                errorCode === 'space_already_exists' || (errorMsg && errorMsg.includes('UniqueViolation') && errorMsg.includes('spc_uix_long_id_oid'))

            if (spaceAlreadyExists) {
                this.errorContent = 'A space with this name already exists in this organization. Please choose a different name.'
                this.error = true
                return
            }

            const globallyHandledErrorCodes = [
                'max_spaces_reached',
                'space_limit_reached',
                'insufficient_org_role',
                'org_missing',
                'space_missing',
                'trial_ended'
            ]
            if (globallyHandledErrorCodes.includes(errorCode) || /space.*_in_.*/.test(errorCode) || /space_.*_migrating/.test(errorCode)) {
                this.error = false
                return
            }

            this.errorContent = 'AN ERROR HAS OCCURRED'
            this.error = true
        },
        updateShortId(newShortId) {
            this.spaceShortName = newShortId
        },
        addReadmeFile(snid) {
            const blob = new Blob([this.readmeFileContent])
            const formData = new FormData()
            formData.append('file', blob, 'README.md')
            formData.append('type', 'text/markdown')
            formData.append('name', 'README.md')
            formData.append('operation', 'CREATE')
            const endpoint = `/snapshots/${snid}/fs/files`
            this.$axios
                .post(endpoint, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                })
                .then(() => {
                    this.$store.dispatch('snapshotStore/fetchCurrentFiles', { id: snid, route: this.$route, setFetchingStatus: true })
                })
                .catch(() => {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText: 'Failed to create the README file.',
                        snackBarTimeout: 5000,
                        snackBarIcon: 'check_circle'
                    })
                })
        },
        addSpace() {
            if (!this.valid) return

            this.error = false
            this.noSpaceDescription = false
            const apiURL = `/orgs/${this.$route.params.oid}/spaces`
            this.loading = true
            const archDate = this.newSpaceType === this.spaceTypes.EDUCATION_SPACE ? this.spaceArchiveByDate : null
            const postBody = {
                description: this.spaceDescription,
                long_id: this.spaceName,
                short_id: this.spaceShortName,
                space_type: this.newSpaceType,
                visibility_type: this.selectedSpaceInformation.code,
                archive_by_date: archDate,
                rtypes: [10, 20, 30, 50, 90]
            }
            if (this.githubURL) postBody.github_url = this.githubURL
            this.$axios
                .post(apiURL, postBody, { timeout: 300000 })
                .then(response => {
                    const spaceData = {
                        oid: response.data.oid,
                        sid: response.data.sid
                    }
                    this.$store.dispatch('showSnackBar', {
                        snackBarText: 'Space created successfully',
                        snackBarTimeout: 5000,
                        snackBarIcon: 'check_circle'
                    })
                    this.$store.dispatch('orgStore/fetchOrgSpaces', this.$route.params.oid).finally(() => {
                        this.$axios.get(`spaces/${response.data.sid}/master/development`).then(response => {
                            const development = response.data
                            if (this.addReadme && !this.githubURL) {
                                this.addReadmeFile(development.snid)
                            }
                            this.$router.push({
                                name: 'snapshot-add-files-hint',
                                params: {
                                    oid: spaceData.oid,
                                    sid: spaceData.sid,
                                    iid: development.iid,
                                    snid: development.snid
                                }
                            })
                        })
                    })
                })
                .catch(error => {
                    this.setSpaceCreationError(error)
                })
                .finally(() => {
                    this.loading = false
                })
        },
        async cloneSpace() {
            if (!this.valid) return

            this.error = false
            this.noSpaceDescription = false
            const apiURL = `/spaces/${this.sidToClone}/copy`
            this.loading = true
            const archDate = this.newSpaceType === this.spaceTypes.EDUCATION_SPACE ? this.spaceArchiveByDate : null
            const postBody = {
                oid: this.$route.params.oid,
                description: this.spaceDescription,
                long_id: this.spaceName,
                short_id: this.spaceShortName,
                archive_by_date: archDate
            }
            try {
                const {
                    data: { oid, sid }
                } = await this.$axios.post(apiURL, postBody, { timeout: 300000 })

                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'The space is being prepared for you.',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'check_circle'
                })
                await this.$store.dispatch('orgStore/fetchOrgSpaces', this.$route.params.oid)

                const {
                    data: { iid, snid }
                } = await this.$axios.get(`spaces/${sid}/master/development`)
                this.$router.push({
                    name: 'snapshot-add-files-hint',
                    params: {
                        oid,
                        sid,
                        iid,
                        snid
                    }
                })
            } catch (error) {
                this.setSpaceCreationError(error)
            }
            this.loading = false
        }
    },
    computed: {
        ...mapState('orgStore', ['fetchingSpaces', 'spacesWithPrimarySnapshots']),
        selectedSpaceInfo() {
            if (this.newSpaceType === this.spaceTypes.EDUCATION_SPACE) {
                return {
                    type: 'Course',
                    readmePlaceholder:
                        'You can add a README file to a course to communicate important information about the course. The README content can be updated anytime and will be automatically shared with students on each distribution.'
                }
            } else if (this.newSpaceType === this.spaceTypes.RESEARCH_SPACE) {
                return {
                    type: 'Research Project',
                    readmePlaceholder:
                        'You can add a README file to a research project to describe and communicate detailed information about the project. The README content can be updated anytime.'
                }
            } else {
                return {
                    type: 'Dataset',
                    readmePlaceholder:
                        'You can add a README file to a dataset to describe detailed information about the dataset. The README content can be updated anytime.'
                }
            }
        },
        selectedSpaceInformation() {
            return this.spaceVisibilityTypes.find(item => item.label === this.spaceVisibility)
        },
        repoURLIsHTTPS() {
            return this.githubURL.toLowerCase().startsWith('https')
        },
        githubURLHint() {
            if (!this.githubURL) return 'Use SSH (git@...) or HTTPS (http://...) URLs.'
            if (!this.repoURLIsHTTPS) return 'Set up your Nuvolos SSH keys (docs.nuvolos.cloud) before cloning via SSH.'
            return 'You can only initialize from public repositories via HTTPS. Use an SSH URL if you plan to push commits from Nuvolos.'
        },
        spacesWithCurrentType() {
            return this.spacesWithPrimarySnapshots.filter(space => {
                return space.space_type === this.newSpaceType && space.space_role === this.roleTypes.SPACE_ADMIN
            })
        }
    },
    watch: {
        spaceSetupMode(mode) {
            this.showCloneOptions = mode === 'template'
            this.addReadme = mode === 'readme'
        },
        showCloneOptions(on) {
            if (on) {
                this.addReadme = false
                return
            }
            this.sidToClone = null
            this.githubURL = null
        },
        cloneRadio(newVal) {
            if (newVal === 'space') {
                this.githubURL = null
            } else {
                this.sidToClone = null
            }
        }
    }
}
</script>

<style scoped lang="scss">
::v-deep #cloneRadio {
    label.v-label {
        flex-direction: column;
        align-items: start;
    }
    div.v-input.v-text-field {
        width: 100%;
    }
}
</style>
